// Phase 26D · UOM master.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const blank = () => ({ code: "", name: "", is_base: false, is_active: true, sort_order: 100 });

export default function UomsPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("masters.manage");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/uoms");
      setRows(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const u = editing.uom;
      if (editing.isNew) {
        await api.post("/uoms", u);
        toast.success(`UOM '${u.code}' created`);
      } else {
        const { code, is_base, ...patch } = u;
        await api.patch(`/uoms/${u.id}`, patch);
        toast.success(`UOM '${u.code}' updated`);
      }
      setEditing(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const doDelete = async (r) => {
    if (!window.confirm(`Delete UOM '${r.code}'? Seeded PCS/KG/BAG/BUNCH cannot be deleted.`)) return;
    try {
      await api.delete(`/uoms/${r.id}`);
      toast.success(`UOM '${r.code}' deleted`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-uoms-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Masters" }, { label: "UOMs" }]}
        title="Units of Measure"
        subtitle="Units you can use when booking an order. Quantities are always converted to pieces and kg."
        favorite={{ key: "admin.uoms", type: "screen", label: "UOMs", route: "/admin/uoms" }}
        testid="uoms-page-header"
        actions={canManage ? (
          <Button data-testid="uom-new" onClick={() => setEditing({ uom: blank(), isNew: true })}
            className="tulsi-gradient text-white h-10 font-semibold rounded-xl shadow-md shadow-[#E5197F]/20">
            <Plus className="h-4 w-4 mr-2" /> New UOM
          </Button>
        ) : null}
      />
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Units</CardTitle>
          <CardDescription>Seeded units (PCS/KG/BAG/BUNCH) can be deactivated but not deleted. Only one base UOM is allowed.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? <div className="flex items-center gap-2 text-sm text-neutral-500 py-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
            : (
              <Table data-testid="uoms-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Base</TableHead>
                    <TableHead>Order</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(r => (
                    <TableRow key={r.id} data-testid={`uom-row-${r.code}`}>
                      <TableCell className="font-mono text-xs">{r.code}</TableCell>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell>{r.is_base ? <Badge className="bg-[#6D2C91]/10 text-[#6D2C91]">Base</Badge> : <span className="text-neutral-400">—</span>}</TableCell>
                      <TableCell className="text-sm">{r.sort_order}</TableCell>
                      <TableCell>{r.is_active ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Active</Badge> : <Badge variant="secondary">Inactive</Badge>}</TableCell>
                      <TableCell className="text-right">
                        {canManage && (
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => setEditing({ uom: { ...r }, isNew: false })}
                              data-testid={`uom-edit-${r.code}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            {!["PCS","KG","BAG","BUNCH"].includes(r.code) && (
                              <Button size="sm" variant="ghost" onClick={() => doDelete(r)}
                                data-testid={`uom-delete-${r.code}`} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="uom-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.isNew ? "Create UOM" : `Edit — ${editing?.uom.code}`}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Code</Label>
                <Input value={editing.uom.code} onChange={(e) => setEditing({ ...editing, uom: { ...editing.uom, code: e.target.value.toUpperCase() } })}
                  disabled={!editing.isNew} placeholder="LITRE" data-testid="uom-code" />
              </div>
              <div>
                <Label>Name</Label>
                <Input value={editing.uom.name} onChange={(e) => setEditing({ ...editing, uom: { ...editing.uom, name: e.target.value } })}
                  data-testid="uom-name" />
              </div>
              <div>
                <Label>Sort order</Label>
                <Input type="number" value={editing.uom.sort_order} onChange={(e) => setEditing({ ...editing, uom: { ...editing.uom, sort_order: Number(e.target.value) || 0 } })}
                  data-testid="uom-sort" />
              </div>
              <div className="flex items-center gap-3">
                <Switch id="uom-active-switch" checked={editing.uom.is_active}
                  onCheckedChange={(v) => setEditing({ ...editing, uom: { ...editing.uom, is_active: v } })}
                  data-testid="uom-active" />
                <Label htmlFor="uom-active-switch">Active</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="uom-save" className="tulsi-gradient text-white font-semibold">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{editing?.isNew ? "Create" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
